// Enums for Assembly
export enum AssemblyStatus {
  SCHEDULED = 'scheduled',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

export enum AssemblyType {
  ORDINARY = 'ordinary',
  EXTRAORDINARY = 'extraordinary',
  INFORMATIVE = 'informative'
}

// Interface for Assembly entity
export interface Assembly {
  id: string;
  title: string;
  description?: string;
  scheduledDate: string; // Fecha como string
  startTime: string; // Hora como string HH:MM
  estimatedDuration: number;
  status: AssemblyStatus;
  type: AssemblyType;
  isVirtual: boolean;
  meetingLink?: string;
  location?: string;
  complex?: string; // Conjunto/complejo para invitaciones selectivas
  quorumRequired: number;
  currentQuorum: number;
  attendeesCount: number;
  actualStartTime?: string;
  actualEndTime?: string;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  code: string; // Código único de acceso
  
  // Related entities (if needed)
  agendaItems?: AgendaItem[];
  participants?: AssemblyParticipant[];
  votingSessions?: VotingSession[];
}

export interface AssemblyOverviewView {
  id: string;
  title: string;
  status: AssemblyStatus;
  type: AssemblyType;
  isVirtual: boolean;
  scheduledDate: string;
  startTime: string;
  estimatedDuration: number;
  meetingLink?: string;
  location?: string;
  code: string;
  connectedParticipantsCount: number;
}

export interface ConnectedParticipantsManagerView {
  assemblyId: string;
  connectedParticipantsCount: number;
}

export interface QuorumManagerView {
  quorumRequired: number;
  quorumAchieved: number;
  attendeeCoefficient: number;
  totalCoefficient: number;
  isQuorumMet: boolean;
  presentUsers?: number;
  representedApartmentsCount?: number;
  missingForQuorum?: number;
}

export interface AssemblySummaryManagerView {
  id: string;
  title: string;
  status: AssemblyStatus;
  type: AssemblyType;
  isVirtual: boolean;
  scheduledDate: string;
  startTime: string;
  estimatedDuration: number;
  meetingLink?: string;
  location?: string;
  code: string;
  connectedParticipantsCount: number;
  quorum: QuorumManagerView;
}

export interface AssemblyScheduledOwnerView {
  id: string;
  title: string;
  description?: string;
  status: AssemblyStatus;
  type: AssemblyType;
  isVirtual: boolean;
  scheduledDate: string;
  startTime: string;
  meetingLink?: string;
  location?: string;
  quorumRequired: number;
}

export type MinutesJobStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface AssemblyMinutesStatus {
  assemblyId: string;
  assemblyExists: boolean;
  hasMinutes: boolean;
  minutesStatus: 'draft' | 'pending_signatures' | 'signed' | 'published' | null;
  pdfReady: boolean;
  pdfPath: string | null;
  jobStatus: MinutesJobStatus | null;
  jobId: string | null;
  jobErrorMessage: string | null;
  jobUpdatedAt: string | null;
}

// Related interfaces (simplified for now)
export interface AgendaItem {
  id: string;
  title: string;
  description?: string;
  order: number;
  estimatedDuration?: number;
  status: 'pending' | 'in_discussion' | 'voting' | 'completed';
  assemblyId: string;
  createdAt: string;
  updatedAt: string;
}

export interface AssemblyParticipant {
  id: string;
  assemblyId: string;
  userId: string;
  isPresent: boolean;
  joinedAt?: string;
  leftAt?: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    apartment?: string;
    role: string;
  };
}

export interface VotingSession {
  id: string;
  title: string;
  description?: string;
  type: 'simple' | 'multiple_choice' | 'secret';
  status: 'pending' | 'active' | 'completed' | 'cancelled';
  startedAt?: string;
  endedAt?: string;
  assemblyId: string;
  agendaItemId?: string;
  options: VotingOption[];
  votes: Vote[];
  createdAt: string;
  updatedAt: string;
}

export interface VotingOption {
  id: string;
  text: string;
  votingSessionId: string;
  voteCount: number;
}

export interface Vote {
  id: string;
  votingSessionId: string;
  userId: string;
  optionId?: string;
  isSecret: boolean;
  createdAt: string;
}

// DTOs for API calls
export interface CreateAssemblyDto {
  title: string;
  description: string;
  scheduledDate: string; // Enviaremos solo la fecha como string YYYY-MM-DD
  startTime: string; // Enviaremos la hora como string HH:MM
  estimatedDuration?: number; // Duración estimada en minutos
  isVirtual: boolean;
  location?: string;
  meetingLink?: string;
  complex?: string;
  selectedComplexes?: string[];
  quorumRequired?: number;
  notificationHours?: number;
  type: AssemblyType;
  agendaItems?: string[];
}

export interface UpdateAssemblyDto {
  title?: string;
  description?: string;
  scheduledDate?: string;
  startTime?: string;
  estimatedDuration?: number;
  isVirtual?: boolean;
  location?: string;
  meetingLink?: string;
  complex?: string;
  selectedComplexes?: string[];
  quorumRequired?: number;
  notificationHours?: number;
  type?: AssemblyType;
  agendaItems?: string[];
}

export interface RescheduleAssemblyDto {
  scheduledDate: string;
  startTime: string;
  reason?: string;
}

export interface AssemblyFilterDto {
  status?: AssemblyStatus;
  type?: AssemblyType;
  hasVirtualLink?: boolean;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

// Stats interface
export interface AssemblyStats {
  totalParticipants: number;
  presentParticipants: number;
  quorumPercentage: number;
  quorumMet: boolean;
  totalVotingSessions: number;
  completedVotingSessions: number;
}